import { Group, Title, Grid, Text } from '@mantine/core';
import { PageWithBreadcrumbs } from '../../components/layout/PageWithBreadcrumbs';
import { ContainerWhite } from '../../components/core';
import React, { useEffect, useState } from 'react';
import { AfaButton, AfaComunicacionsCard, AfaDatePickerInput, AfaSectionLoader, AfaTextInput } from '../../components/ui';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

// Página de comunicaciones - Gestión de comunicaciones oficiales
const Comunicaciones = (props: any) => {
  const navigate = useNavigate();
  
  const [communications, setCommunications] = useState([] as any[]);
  const [filteredCommunications, setFilteredCommunications] = useState([] as any[]);
  const [loading, setLoading] = useState(true);

  const [dataDesDe, setDataDesDe] = useState<Date | null>(null);
  const [dataFinsA, setDataFinsA] = useState<Date | null>(null);
  const [searchText, setSearchText] = useState<string>('');

  // Normaliza una cadena: quita acentos/diacríticos y caracteres especiales, deja sólo letras/números/espacios en minúsculas
  const normalizeString = (input?: string) => {
    if (!input) return '';
    try {
      // eliminar diacríticos
      const withoutDiacritics = input.normalize('NFD').replace(/\p{Diacritic}/gu, '');
      // pasar a minúsculas y eliminar caracteres no alfanuméricos (conservar espacios)
      return withoutDiacritics.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
    } catch (e) {
      // Fallback para entornos que no soporten \p{Diacritic}
      const fallback = input.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      return fallback.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
    }
  };

  useEffect(() => {
    props.apiService.GetComunicaciones()
      .then((data: any) => {
        setCommunications(data);
        setFilteredCommunications(data);
        setLoading(false);
      })
      .catch((error: any) => {
        console.error("Error fetching comunicaciones:", error);
        setLoading(false);
      })
  }, []);

  const handleAplicarFiltres = async () => {
    // Aplicar filtros sobre el conjunto cargado en `communications`.
    try {
      const normalizedSearch = normalizeString(searchText);

      const filtered = communications.filter((comm: any) => {
        if (!comm) return false;

        // Si existe la propiedad isActive y es false, lo excluimos
        if (comm.hasOwnProperty('isActive') && !comm.isActive) return false;

        // Filtrado por rango de fechas (inclusive) usando moment
        if (dataDesDe) {
          if (!comm.communicationDate || !moment(comm.communicationDate).isSameOrAfter(moment(dataDesDe).startOf('day'))) {
            return false;
          }
        }

        if (dataFinsA) {
          if (!comm.communicationDate || !moment(comm.communicationDate).isSameOrBefore(moment(dataFinsA).endOf('day'))) {
            return false;
          }
        }

        // Filtrado por texto en título o descripción (ignorando acentos y caracteres especiales)
        if (normalizedSearch) {
          const normalizedTitle = normalizeString(comm.title);
          const normalizedDescription = normalizeString(comm.description);

          const inTitle = normalizedTitle.includes(normalizedSearch);
          const inDescription = normalizedDescription.includes(normalizedSearch);

          if (!inTitle && !inDescription) return false;
        }

        return true;
      });

      setFilteredCommunications(filtered);
    } catch (err) {
      console.error('Error applying filters to comunicaciones:', err);
    }
  };

  const handleEsborrarFiltres = () => {
    setDataDesDe(null);
    setDataFinsA(null);
    setSearchText('');
    // Restaurar la lista completa cargada originalmente
    setFilteredCommunications(communications);
  };

  return (
    <PageWithBreadcrumbs>
      <Title order={1}>Comunicacions</Title>
      <ContainerWhite>
        <Title order={3}>
          Filtres
        </Title>

        <Grid mt="md">
          <Grid.Col span={6}>
            <AfaTextInput
              label="Cerca"
              placeholder="Escriu el text a cercar"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              inputProps={{
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    // Ejecutar búsqueda al pulsar Enter
                    handleAplicarFiltres();
                  }
                }
              }}
              name="cerca"
              id="cerca"
            />
          </Grid.Col>

          <Grid.Col span={3}>
            <AfaDatePickerInput
              label="Data de presentació des de"
              placeholder="Selecciona data"
              value={dataDesDe}
              onChange={setDataDesDe}
              name="dataDesDe"
              id="data-des-de"
            />
          </Grid.Col>

          <Grid.Col span={3}>
            <AfaDatePickerInput
              label="Fins a"
              placeholder="Selecciona data"
              value={dataFinsA}
              onChange={setDataFinsA}
              name="dataFinsA"
              id="data-fins-a"
            />
          </Grid.Col>


        </Grid>

        {/* Cuarta línea: Botones de acción */}
        <Group justify="flex-end" gap={16} mt="lg">
          <AfaButton
            id="btn-esborrar-filtres"
            variant="outline"
            onClick={handleEsborrarFiltres}
          >
            Esborrar filtres
          </AfaButton>
          <AfaButton
            id="btn-cercar"
            variant="filled"
            onClick={handleAplicarFiltres}
          >
            Cercar
          </AfaButton>
        </Group>

        {loading ? (
          <AfaSectionLoader message="Carregant comunicacions..." />
        ) : (
          <div>
            <Group gap={8} mt="md">
              <Text size="xl" fw={600}>{filteredCommunications.length}</Text>
              <Text size="md"> {filteredCommunications.length === 1 ? 'Resultat' : 'Resultats'}</Text>
            </Group>

            {filteredCommunications.map((comm: any) => (
              <AfaComunicacionsCard
                key={comm.id || comm.title} // importante añadir una key única
                title={comm.title}
                description={comm.description}
                data={moment(comm.communicationDate.toString()).format('DD/MM/YYYY')}
                onClick={() => {
                  const encodedTitle = encodeURIComponent(comm.title);
                  navigate(`/comunicaciones/detall/${encodedTitle}`);
                }}
              />
            ))}
          </div>
        )}
      </ContainerWhite>
    </PageWithBreadcrumbs>
  );
};

export default Comunicaciones;
